import {clsx} from 'clsx'
import {useCallback, useMemo, useState} from 'react'
import type {CSSProperties, ReactNode} from 'react'
import {AccordionActionsContext, AccordionConfigContext, AccordionStateContext} from './accordion-context'
import type {AccordionChevronPlacement, AccordionType} from './accordion-context'
import {AccordionHeader} from './AccordionHeader'
import {AccordionItem} from './AccordionItem'
import {AccordionPanel} from './AccordionPanel'
import styles from './Accordion.module.css'

const EMPTY_VALUES: readonly string[] = []

interface AccordionBaseProps {
  /** `single` keeps at most one item open, `multiple` allows any number. */
  type?: AccordionType
  /** Called with the full next set of open items. */
  onValueChange?: (value: string[]) => void
  /** Draw a divider between adjacent items. */
  dividers?: boolean
  /** Which side of the header label the chevron sits on. */
  chevronPlacement?: AccordionChevronPlacement
  className?: string
  style?: CSSProperties
  children?: ReactNode
}

interface ControlledAccordionProps extends AccordionBaseProps {
  /** Open item value(s). Makes the accordion controlled; pair with `onValueChange`. */
  value: string | string[]
  defaultValue?: never
}

interface UncontrolledAccordionProps extends AccordionBaseProps {
  value?: never
  /** Initially open item value(s) for an uncontrolled accordion. */
  defaultValue?: string | string[]
}

export type AccordionProps = ControlledAccordionProps | UncontrolledAccordionProps

const toValueArray = (value: string | string[] | undefined): readonly string[] => {
  if (value === undefined) return EMPTY_VALUES
  return Array.isArray(value) ? value : [value]
}

/** `single` never holds more than one open value, whatever the caller passed in. */
const normalizeValues = (values: readonly string[], type: AccordionType): string[] =>
  type === 'single' ? values.slice(0, 1) : [...values]

const isSameValues = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index])

const nextValuesFor = (current: readonly string[], value: string, nextOpen: boolean, type: AccordionType): string[] => {
  if (!nextOpen) return current.filter(candidate => candidate !== value)
  if (type === 'single') return [value]
  return current.includes(value) ? [...current] : [...current, value]
}

const AccordionRoot = ({
  type = 'single',
  value,
  defaultValue,
  onValueChange,
  dividers = true,
  chevronPlacement = 'trailing',
  className,
  style,
  children,
}: AccordionProps) => {
  const [uncontrolledValues, setUncontrolledValues] = useState<string[]>(() =>
    normalizeValues(toValueArray(defaultValue), type),
  )
  const [previousType, setPreviousType] = useState(type)

  const isControlled = value !== undefined
  const controlledValues = useMemo(
    () => (value === undefined ? null : normalizeValues(toValueArray(value), type)),
    [value, type],
  )
  const normalizedUncontrolledValues = useMemo(
    () => normalizeValues(uncontrolledValues, type),
    [uncontrolledValues, type],
  )

  // When the accordion switches from `multiple` to `single`, trim the open items during render.
  // This avoids a brief state where two panels are open at once and prevents stale items from
  // reappearing when the mode switches back.
  if (previousType !== type) {
    setPreviousType(type)
    if (!isSameValues(uncontrolledValues, normalizedUncontrolledValues)) {
      setUncontrolledValues(normalizedUncontrolledValues)
    }
  }

  const values = controlledValues ?? normalizedUncontrolledValues
  const openValues = useMemo<ReadonlySet<string>>(() => new Set(values), [values])

  // Find-in-page can unhide a closed panel
  // Sync that open into Accordion state here so the found panel stays visible
  const setItemOpen = useCallback(
    (itemValue: string, nextOpen: boolean) => {
      const next = nextValuesFor(values, itemValue, nextOpen, type)
      if (isSameValues(next, values)) return
      if (!isControlled) setUncontrolledValues(next)
      onValueChange?.(next)
    },
    [values, type, isControlled, onValueChange],
  )

  const config = useMemo(() => ({chevronPlacement}), [chevronPlacement])
  const actions = useMemo(() => ({setItemOpen}), [setItemOpen])

  return (
    <AccordionConfigContext value={config}>
      <AccordionActionsContext value={actions}>
        <AccordionStateContext value={openValues}>
          <div className={clsx(styles.Accordion, dividers && styles.dividers, className)} style={style}>
            {children}
          </div>
        </AccordionStateContext>
      </AccordionActionsContext>
    </AccordionConfigContext>
  )
}

AccordionRoot.displayName = 'Accordion'

export const Accordion = Object.assign(AccordionRoot, {
  Item: AccordionItem,
  Header: AccordionHeader,
  Panel: AccordionPanel,
})
